/**
 * Sender resume — the §5.1 scan + §5.2 reconcile-and-continue algorithm
 * (docs/file-transfer-resume.md). Pure `SenderResumeDeps`-injected core,
 * fully unit-testable with fakes (see __tests__/senderResume.test.ts);
 * `senderResumeDeps.ext.ts` supplies the real OPFS/HTTP/Web-Locks/crypto
 * wiring. Shares `uploadLoop.ts`'s `runUploadLoop` with `sendFile.ts` — the
 * spec's "one code path" requirement (§3.1.4 point 3).
 *
 * §2.4 load-time validation (drop on `v !== 1` / unknown `status` / invalid
 * manifest for an active record) is applied HERE, not in `senderState.ts`
 * (a plain CRUD layer) — this module already holds the OPFS deps needed to
 * delete a dropped record's write-through copy.
 *
 * SECURITY: never log file name/content/manifest fields — security.md.
 */

import type { FileCtrl, FtErrorCode, TransferManifest } from "@reflecto/core";
import { decodeBase64, validateManifest } from "@reflecto/core";
import type { SendControlDeps } from "./activeSendControl";
import { isAlreadyComplete, isDefinitive404, isNetworkError } from "./errors";
import { type FtTerminalOutcome, isKnownTransferStatus, isTerminalSenderStatus } from "./lifecycle";
import type { FtTransport } from "./p2pTypes";
import { partPlan } from "./partPlan";
import type { SenderCrypto, SenderSource, SendProgressEvent, TransferApi } from "./sendFile";
import type { FtSenderStateRecord } from "./state";
import {
  PartUploadError,
  runUploadLoop,
  UploadCancelledError,
  UploadPausedError,
  UploadPeerFailedError,
} from "./uploadLoop";

/** `GET /v1/transfers/:id`'s reconciliation snapshot (background/api.ts's TransferStatusResponse, narrowed to what resume needs). */
export interface ResumeTransferStatus {
  status: string;
  partCount: number;
  partsUploaded: number[];
  expiresAt: number;
}

export interface ResumeApi
  extends Pick<
    TransferApi,
    "uploadUrl" | "putPart" | "markUploaded" | "complete" | "deleteTransfer"
  > {
  getTransfer(transferId: string): Promise<ResumeTransferStatus>;
}

/** Every side effect the resume algorithm needs, injected for testability (see module doc). */
export interface SenderResumeDeps {
  /** Raw (unvalidated) records — §2.4 validation runs inside this module. */
  listSenderStates(): Promise<FtSenderStateRecord[]>;
  saveSenderState(record: FtSenderStateRecord): Promise<void>;
  removeSenderState(transferId: string): Promise<void>;
  /**
   * Acquire the single-writer lock for `transferId` (§3.1.2's Web Lock,
   * `{ ifAvailable: true }`). `false` means another context is already
   * driving this transfer — skip silently.
   */
  acquireLock(transferId: string): Promise<boolean>;
  /** Release a previously-acquired lock (best-effort — locks also auto-release on context death). */
  releaseLock(transferId: string): Promise<void>;
  api: ResumeApi;
  sendCtrl(peerDeviceId: string, ctrl: FileCtrl): Promise<void>;
  /** Open the OPFS write-through copy for a record's transferId + fileIndex (mirrors sendFile.ts's SenderDeps.openSource). */
  openSource(transferId: string, fileIndex: number): SenderSource;
  crypto: Pick<SenderCrypto, "deriveTransferKey" | "encryptChunk">;
  /** Raw X25519 shared secret for (own secret key, peerPub) — re-derived, never persisted (§2.5). */
  deriveSharedSecret(peerPub: Uint8Array): Promise<Uint8Array>;
  senderPublicKey: Uint8Array;
  now?(): number;
  /** §3.1.3 unpair-abort guard — checked between records/steps; true stops the scan without further writes/ctrls. */
  isAborted?(): boolean;
  /** Pause/cancel/peer-progress control seam (TASK-19) — mirrors sendFile.ts's SenderDeps.control (same activeSendControl.ts singleton). */
  control?: SendControlDeps;
  /** The RECEIVER's inbound progress ctrl (§8.2 "UI only" row), monotonically clamped by the control registry before this fires. */
  onPeerProgress?(transferId: string, chunksDone: number): void;
  /**
   * UI progress events for a CARD-initiated resume (TASK-19) — the same
   * `SendProgressEvent` stream `sendFile`'s fresh send emits, so the panel's
   * SendFileCard can keep tracking a transfer it resumed after the original
   * `send()` call settled. Optional — the panel-mount scan and inbound
   * resume-ctrl paths omit it (no card is watching a specific transfer there).
   */
  onProgress?(event: SendProgressEvent): void;
}

/** §2.4 drop-on-load validation. Returns the parsed manifest for an active record kept valid, or null if the record was dropped. */
function validateRecord(record: FtSenderStateRecord): TransferManifest | null {
  if (record.v !== 1) return null;
  if (!isKnownTransferStatus(record.status)) return null;
  if (isTerminalSenderStatus(record.status)) return {} as TransferManifest; // terminal tombstones are exempt from manifest validation
  try {
    const manifest = JSON.parse(record.manifestJson) as TransferManifest;
    if (validateManifest(manifest) !== null) return null;
    return manifest;
  } catch {
    return null;
  }
}

async function now(deps: SenderResumeDeps): Promise<number> {
  return deps.now ? deps.now() : Date.now();
}

/**
 * Best-effort file count off a §2.4-dropped record's raw `manifestJson`, used ONLY to know
 * how many per-file OPFS sources to clean up (`resumePendingSends`'s drop path, below) — the
 * manifest may itself be unparseable/malformed (that can be exactly WHY the record was
 * dropped), so this never throws and falls back to `1` (matches every real-world case today).
 */
export function bestEffortFileCount(manifestJson: string): number {
  try {
    const parsed = JSON.parse(manifestJson) as { files?: unknown };
    if (Array.isArray(parsed.files) && parsed.files.length > 0) return parsed.files.length;
  } catch {
    // fall through to the single-file default
  }
  return 1;
}

/**
 * Exported (docs/file-transfer-p2p.md §9.3/§16 pt.4) so `sendFile.ts`'s post-demotion
 * `demotedFromP2p` early-complete override can reuse this EXACT record-mutation + save shape
 * (rather than duplicating it) when a late-arriving verified `complete` ctrl aborts an in-flight
 * relay reconcile early.
 */
export async function persistTerminal(
  record: FtSenderStateRecord,
  status: FtTerminalOutcome,
  errorCode: FtErrorCode | undefined,
  deps: SenderResumeDeps,
  /** Only meaningful on "complete" — `file_transfer_completed` is the event that carries transport. */
  transport: FtTransport = "relay"
): Promise<void> {
  record.status = status;
  record.errorCode = errorCode;
  record.updatedAt = await now(deps);
  await deps.saveSenderState(record);
  // UI event AFTER the persist (mirrors sendFile.ts's notify-after-save
  // ordering). "expired"/"failed" both surface as the generic upload failure
  // reason bucket, but `errorCode` (TASK-30) rides along so the UI can show a
  // code-specific string; "cancelled" gets its own reason (no code — not a
  // protocol error) so the card can label it distinctly.
  if (status === "complete") {
    deps.onProgress?.({ stage: "done", transferId: record.transferId, transport });
  } else {
    deps.onProgress?.({
      stage: "failed",
      reason: status === "cancelled" ? "cancelled" : "upload-failed",
      transferId: record.transferId,
      errorCode,
      // Resume drives an already-created transfer, so anything terminal here died
      // with bytes in flight — never during pre-flight, which only the initial
      // live send performs.
      failStage: "transfer",
    });
  }
}

/**
 * Checkpoint-resolved P-S3 (pause): the loop already stopped cleanly after a
 * completed part's P-S2 persist — no in-flight bytes to abort, no ctrl ever
 * sent, OPFS copy kept for the eventual resume.
 */
async function persistPaused(record: FtSenderStateRecord, deps: SenderResumeDeps): Promise<void> {
  record.status = "paused";
  record.updatedAt = await now(deps);
  await deps.saveSenderState(record); // P-S3
  deps.onProgress?.({ stage: "paused", transferId: record.transferId });
}

/** Best-effort delete of every opened per-file OPFS copy (multi-file transfer design §5) — never throws. */
async function deleteAllSources(sources: readonly SenderSource[]): Promise<void> {
  await Promise.all(sources.map((s) => s.deleteCopy().catch(() => {})));
}

/**
 * Cancel cleanup for an ACTIVE loop's `UploadCancelledError` (§8.2/§9.1) — a
 * LOCAL cancel echoes an outbound ctrl; a PEER-initiated one (the inbound
 * `cancel` ctrl that requested it) does not, since the peer already knows.
 */
async function cancelActive(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps,
  sources: readonly SenderSource[],
  origin: "local" | "peer" | undefined
): Promise<void> {
  if (origin === "local") {
    await deps
      .sendCtrl(record.peerDeviceId, {
        kind: "cancel",
        transferId: record.transferId,
        reason: "user",
      })
      .catch(() => {});
  }
  await deps.api.deleteTransfer(record.transferId).catch(() => {});
  await persistTerminal(record, "cancelled", undefined, deps);
  await deleteAllSources(sources);
}

/**
 * Peer-reported terminal failure for an ACTIVE loop (§8.2's `error` row).
 * No echoed ctrl — the receiver is the one that told us. No `DELETE` either:
 * the receiver already deleted for HASH_MISMATCH/CHUNK_AUTH_FAIL/STORAGE_FULL,
 * and §8.2 notes a second delete would merely 404. Terminal is `"failed"`
 * carrying the PEER's code, not our own INTERNAL.
 */
async function peerFailedActive(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps,
  sources: readonly SenderSource[]
): Promise<void> {
  // Already an FtErrorCode — isFileCtrl normalized it at the wire boundary.
  const code = deps.control?.getPeerErrorCode(record.transferId) ?? "INTERNAL";
  await persistTerminal(record, "failed", code, deps);
  await deleteAllSources(sources);
}

/** §5.2 step 1 failure / partCount mismatch — terminal "failed"/"INTERNAL", best-effort error ctrl + DELETE, P-S4. */
async function failInternal(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps,
  sources: readonly SenderSource[]
): Promise<void> {
  await deps
    .sendCtrl(record.peerDeviceId, {
      kind: "error",
      transferId: record.transferId,
      code: "INTERNAL",
      message: "",
    })
    .catch(() => {});
  await deps.api.deleteTransfer(record.transferId).catch(() => {});
  await persistTerminal(record, "failed", "INTERNAL", deps);
  await deleteAllSources(sources);
}

/** §7.2 EXPIRED path — DEFINITIVE_404 outside CREATE_WAIT (sender never enters CREATE_WAIT — that's receiver-only). */
async function expireTransfer(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps,
  sources: readonly SenderSource[]
): Promise<void> {
  await persistTerminal(record, "expired", "EXPIRED", deps);
  await deleteAllSources(sources);
  await deps
    .sendCtrl(record.peerDeviceId, {
      kind: "error",
      transferId: record.transferId,
      code: "EXPIRED",
      message: "",
    })
    .catch(() => {});
}

/** Send the (duplicate-safe) complete ctrl + P-S4, WITHOUT calling POST /complete — used when the server already knows the transfer is complete. */
async function finishAlreadyComplete(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps,
  sources: readonly SenderSource[],
  transport: FtTransport = "relay"
): Promise<void> {
  await deps
    .sendCtrl(record.peerDeviceId, {
      kind: "complete",
      transferId: record.transferId,
      verified: true,
    })
    .catch(() => {});
  await persistTerminal(record, "complete", undefined, deps, transport);
  await deleteAllSources(sources);
}

/** Step 6 — the upload set just drained: call POST /complete for the first time, then finish exactly like `finishAlreadyComplete`. */
async function finishAfterUpload(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps,
  sources: readonly SenderSource[],
  transport: FtTransport = "relay"
): Promise<void> {
  try {
    await deps.api.complete(record.transferId);
  } catch (err) {
    if (!isAlreadyComplete(err)) throw err; // a concurrent completion elsewhere — tolerate, proceed to finish
  }
  await finishAlreadyComplete(record, deps, sources, transport);
}

/**
 * §5.2 step 5's ALREADY_COMPLETE route — "re-GET; status 'complete' → jump
 * to step 2's complete branch". The fresh GET is mandatory (the 409 body is
 * a signal, not a snapshot); its failures follow step 2's own rules:
 * DEFINITIVE_404 → §7.2 EXPIRED; NETWORK (or anything else) → leave the
 * record untouched and stop (§7.3 never-expire-locally — next scan retries).
 * A 200 that is NOT "complete" contradicts the ALREADY_COMPLETE signal
 * (unreachable given the server's state machine, which never un-completes) —
 * leave the record "transferring" and let the next scan re-reconcile from
 * scratch rather than looping inside this pass.
 */
async function handleAlreadyComplete(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps,
  sources: readonly SenderSource[],
  transport: FtTransport = "relay"
): Promise<void> {
  let recheck: ResumeTransferStatus;
  try {
    recheck = await deps.api.getTransfer(record.transferId);
  } catch (err) {
    if (isDefinitive404(err)) await expireTransfer(record, deps, sources);
    return;
  }
  if (recheck.status === "complete") {
    await finishAlreadyComplete(record, deps, sources, transport);
  }
}

/**
 * §5.2 reconcile-and-continue — one full pass over a single `"transferring"`
 * record. Also the entry point for an explicit `"paused"` -> resume
 * transition (§9.1) once a caller has already checked the status gate.
 *
 * Exported (docs/file-transfer-p2p.md §16 pt.4 — "the §5.2 core") so a P2P
 * demotion can call it DIRECTLY, inside the Web Lock the demoting context
 * already holds, instead of re-entering via `resumeSenderTransfer`'s own
 * `{ ifAvailable: true }` lock acquisition — which would see the lock as
 * already held by this very context and silently skip (a guaranteed bug).
 * Callers reusing this export MUST already hold `transferId`'s Web Lock.
 */
export async function reconcileAndContinue(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps,
  /**
   * True when this reconcile is the handoff from a demoted P2P attempt. Mirrors
   * `receiveResume.reconcileAndContinue`'s flag — without it the sender reports a
   * post-demotion completion as a plain "relay" and the demote becomes invisible on
   * the sender half.
   */
  afterDemote = false
): Promise<void> {
  const transport: FtTransport = afterDemote ? "relay_after_demote" : "relay";

  const manifest = JSON.parse(record.manifestJson) as TransferManifest;
  // One OPFS write-through copy per file (multi-file transfer design §5), indexed
  // identically to `manifest.files` — a single-file transfer opens exactly one.
  const sources: SenderSource[] = manifest.files.map((_file, i) =>
    deps.openSource(record.transferId, i)
  );

  // Step 1 — source check: EVERY file's durable copy must exist at its manifest size.
  for (let i = 0; i < manifest.files.length; i++) {
    const size = await sources[i].getSize();
    if (size === null || size !== manifest.files[i].size) {
      await failInternal(record, deps, sources);
      return;
    }
  }

  // Step 2 — GET /v1/transfers/:id.
  let status: ResumeTransferStatus;
  try {
    status = await deps.api.getTransfer(record.transferId);
  } catch (err) {
    if (isDefinitive404(err)) {
      await expireTransfer(record, deps, sources);
      return;
    }
    // NETWORK (or anything else at this step) — never expires state locally; leave untouched, stop.
    return;
  }

  if (status.status === "complete") {
    // Crashed after POST /complete — re-send the (duplicate-safe) complete ctrl and P-S4.
    await finishAlreadyComplete(record, deps, sources, transport);
    return;
  }

  if (status.partCount !== record.partCount) {
    await failInternal(record, deps, sources);
    return;
  }

  // Step 3 — server wins, both directions.
  record.partsUploaded = [...status.partsUploaded].sort((a, b) => a - b);
  record.updatedAt = await now(deps);
  await deps.saveSenderState(record);

  const uploaded = new Set(status.partsUploaded);
  const uploadSet: number[] = [];
  for (let p = record.skipBelowPart; p < record.partCount; p++) {
    if (!uploaded.has(p)) uploadSet.push(p);
  }

  // Register the ACTIVE control seam from here on — the record is durably
  // "transferring" (§8.2's "Active record" ctrl-table column) whether or not
  // an upload loop actually runs below (the empty-uploadSet finish path is
  // near-instant, but a pause/cancel ctrl racing it should still find an
  // entry rather than being dropped as "no record").
  deps.control?.register(record.transferId, (chunksDone) =>
    deps.onPeerProgress?.(record.transferId, chunksDone)
  );
  try {
    if (uploadSet.length === 0) {
      await finishAfterUpload(record, deps, sources, transport);
      return;
    }

    // Step 4 — re-derive transferKey.
    const peerPub = decodeBase64(record.peerPub);
    const sharedSecret = await deps.deriveSharedSecret(peerPub);
    const transferKey = await deps.crypto.deriveTransferKey(
      sharedSecret,
      record.transferId,
      deps.senderPublicKey,
      peerPub
    );
    const noncePrefix = decodeBase64(manifest.noncePrefix);
    // File-aligned global-part -> per-file chunk-range map (multi-file transfer design §4) —
    // for a single-file record this degenerates to the old chunkCount-only math.
    const plan = partPlan(manifest.files);

    // Step 5 — upload loop (shared with sendFile.ts's fresh-send pass 2).
    try {
      await runUploadLoop(uploadSet, {
        api: deps.api,
        crypto: deps.crypto,
        plan,
        sourceFor: (fileIndex) => sources[fileIndex],
        fileChunkCounts: manifest.files.map((f) => f.chunkCount),
        transferKey,
        noncePrefix,
        transferId: record.transferId,
        partCount: record.partCount,
        onPartUploaded: async (partIndex) => {
          record.partsUploaded = [...record.partsUploaded, partIndex].sort((a, b) => a - b);
          record.updatedAt = await now(deps);
          await deps.saveSenderState(record); // P-S2
        },
        onProgress: (partIndex, totalParts) =>
          deps.onProgress?.({
            stage: "uploading",
            partIndex,
            partCount: totalParts,
            transferId: record.transferId,
          }),
        checkInterrupt: () => deps.control?.checkInterrupt(record.transferId),
      });
    } catch (err) {
      if (err instanceof UploadPausedError) {
        await persistPaused(record, deps);
        return;
      }
      if (err instanceof UploadCancelledError) {
        const origin = deps.control?.getCancelOrigin(record.transferId);
        await cancelActive(record, deps, sources, origin);
        return;
      }
      if (err instanceof UploadPeerFailedError) {
        await peerFailedActive(record, deps, sources);
        return;
      }
      if (isAlreadyComplete(err)) {
        await handleAlreadyComplete(record, deps, sources, transport);
        return;
      }
      if (isDefinitive404(err)) {
        await expireTransfer(record, deps, sources);
        return;
      }
      if (isNetworkError(err)) return; // leave "transferring", next scan retries (§7.3)
      // Everything else is terminal "failed"/INTERNAL (§5.2 step 5): a
      // PartUploadError (remote retry budget exhausted) OR a non-retryable
      // LOCAL failure — an OPFS read/crypto error escaping encryptPart (which
      // runs OUTSIDE uploadPartWithRetry's per-attempt try), or a state-write
      // failure from onPartUploaded. Local errors must NOT fall through to a
      // bare return: that would strand the record "transferring" forever with
      // no user signal, since every later scan re-hits the same local fault.
      await deps
        .sendCtrl(record.peerDeviceId, {
          kind: "error",
          transferId: record.transferId,
          code: "INTERNAL",
          message:
            err instanceof PartUploadError
              ? "part upload failed after retries"
              : "sender-local failure during upload",
        })
        .catch(() => {});
      await deps.api.deleteTransfer(record.transferId).catch(() => {});
      await persistTerminal(record, "failed", "INTERNAL", deps);
      await deleteAllSources(sources);
      return;
    }

    // Step 6 — uploadSet drained.
    await finishAfterUpload(record, deps, sources, transport);
  } finally {
    deps.control?.deregister(record.transferId);
  }
}

/**
 * Explicit single-record resume — the entry point for a `"paused"` record's
 * user-initiated (or peer `resume` ctrl) transition back to `"transferring"`
 * (§9.1). Runs the SAME algorithm as crash recovery (§5.1/§5.2 — "there is
 * ONE resume code path").
 */
export async function resumeSenderTransfer(
  record: FtSenderStateRecord,
  deps: SenderResumeDeps
): Promise<void> {
  const gotLock = await deps.acquireLock(record.transferId);
  if (!gotLock) return;
  try {
    // §9.1 PAUSED -> resume -> TRANSFERRING_RELAY: statuses map onto states
    // (§9 header), so once resumed the record must persist as
    // "transferring" — flipped in memory here and written by the algorithm's
    // FIRST natural persist (step 3's server-wins save). Deliberately no
    // extra write point: a NETWORK failure at step 2 leaves the DISK record
    // "paused" (§5.2 step 2's "or 'paused' if that's how we got here"), so a
    // resume that never reached the server doesn't flip the record into the
    // auto-resume pool.
    if (record.status === "paused") record.status = "transferring";
    await reconcileAndContinue(record, deps);
  } finally {
    await deps.releaseLock(record.transferId);
  }
}

/**
 * §5.1 scan — the panel-mount resume trigger. Loads every sender record,
 * drops invalid ones (§2.4), skips terminal and `"paused"` records (a crash
 * while paused must never auto-resume), and reconciles every
 * `"transferring"` record under its single-writer lock.
 */
export async function resumePendingSends(deps: SenderResumeDeps): Promise<void> {
  const rawRecords = await deps.listSenderStates();

  for (const record of rawRecords) {
    if (deps.isAborted?.()) return;

    const manifest = validateRecord(record);
    if (manifest === null) {
      // §2.4 drop: delete the record AND its artifacts (every file's OPFS write-through
      // copy) — a dropped record must not leave an orphan. The manifest itself may be
      // unparseable (that can be exactly WHY it was dropped), so the file count is read
      // defensively from the raw JSON — falling back to a single source (index 0) only
      // when it can't be parsed. `validateManifest` now accepts 1..MAX_FILES files, so a
      // multi-file record can exist; bestEffortFileCount returns its real length.
      const fileCount = bestEffortFileCount(record.manifestJson);
      const droppedSources = Array.from({ length: fileCount }, (_unused, i) =>
        deps.openSource(record.transferId, i)
      );
      await deleteAllSources(droppedSources);
      await deps.removeSenderState(record.transferId);
      continue;
    }
    if (isTerminalSenderStatus(record.status)) continue; // pruning is senderState.ts's job (§8.5)
    if (record.status !== "transferring") continue; // "paused" — explicit resume only

    const gotLock = await deps.acquireLock(record.transferId);
    if (!gotLock) continue; // another context is already driving it

    try {
      await reconcileAndContinue(record, deps);
    } catch {
      // §5.1 per-record fault isolation: one record's failing reconcile (a
      // non-ALREADY_COMPLETE error from POST /complete, a network blip) must
      // not starve the remaining pending transfers in this scan pass. The
      // record stays "transferring" and the next scan retries it. Headless
      // module: silent by design — the caller decides what to log.
    } finally {
      await deps.releaseLock(record.transferId);
    }
  }
}
